import {Container} from 'react-smooth-dnd';
import React, {useCallback} from 'react';
import cx from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {WarehouseHeaderContainer} from '../WarehouseHeader/WarehouseHeader';
import {WarehouseFooter} from './WarehouseFooter';
import {Inventory} from '../Inventory';
import {Overlay} from '../Overlay/Overlay';
import {
    sendDeleteWarehouseRequest,
    removeInventoryFromWarehouse,
    reOrderInventoryPosition,
    sendMoveInventoryToWarehouseFullyRequest, boardSlice,
} from '../../store/slices/boardSlice';
import {EditWarehouseModal} from '../WarehouseHeader/EditWarehouseModal';
import s from './Warehouse.module.css';
import {Draggable} from '../Draggable';
import {changeIsDragAvailable} from '../../store/slices/warehouseSlice';
import {getWarehouseTotalInfoRequest} from '../../store/slices/warehouseTotalInfoSlice';
import {
    changeIsMoveInventoryQuantityModalOpened, setIsDragAndDropMovingMode, setWarehouseFrom, setWarehouseTo,
} from '../../store/slices/moveInventoryQuantityModalSlice';

const Card = React.memo(
    ({
        warehouseData,
        warehouseIndex,
        isEditWarehouseMode,
        isRenameWarehouseMode,
        reOrderInventory,
        onEditWarehouseButtonClick,
        onWarehouseRename,
        draggable,
    }) => {
        const dispatch = useDispatch();
        const filterValue = useSelector((state) => state.filter);

        const data = warehouseData.items.filter((item) => {
            if (!filterValue) {
                return item;
            }

            return item.title.toLowerCase().includes(filterValue.toLowerCase());
        });

        const onInventoryDrop = ({ removedIndex, addedIndex, payload }) => {
            if (removedIndex == null && addedIndex === null) {
                return;
            }

            if (removedIndex !== null && addedIndex !== null) {
                if (!filterValue) {
                    reOrderInventory({
                        newIndex: addedIndex,
                        oldIndex: removedIndex,
                        warehouseId: warehouseData.id,
                    });
                } else {
                    const args = {
                        warehouseId: warehouseData.id,
                        oldIndex: payload.sort_index,
                    };
                    if (addedIndex > 0) {
                        args.newIndex = data[addedIndex].sort_index;
                    } else {
                        args.newIndex = 0;
                    }
                    dispatch(reOrderInventoryPosition(args));
                }

                return;
            }
            if (removedIndex !== null) {
                const args = {
                    warehouseId: warehouseData.id,
                };
                if (!filterValue) {
                    args.inventoryPosition = removedIndex;
                } else {
                    args.inventoryPosition = payload.sort_index;
                }

                dispatch(removeInventoryFromWarehouse(args));
                if (payload.amount === 1) {
                    setTimeout(() => {
                        dispatch(getWarehouseTotalInfoRequest(warehouseData.id));
                    }, 1000);
                }
            }
            if (addedIndex !== null) {
                dispatch(setIsDragAndDropMovingMode(true));
                dispatch(setWarehouseTo(warehouseData));

                if (warehouseData.items.length === 0) {
                    addedIndex = 0;
                }
                const args = {
                    inventory: payload,
                    warehouseId: warehouseData.id,
                };
                if (!filterValue) {
                    args.inventoryPosition = addedIndex;
                } else if (addedIndex > 0) {
                    args.inventoryPosition = data[addedIndex - 1].sort_index + 1;
                } else {
                    args.inventoryPosition = 0;
                }
                if (payload.amount > 1) {
                    dispatch(boardSlice.actions.moveInventoryToWarehouse(args));
                    dispatch(changeIsMoveInventoryQuantityModalOpened({
                        value: true,
                        inventory: args.inventory,
                        inventoryPosition: args.inventoryPosition,
                    }));
                } else {
                    dispatch(sendMoveInventoryToWarehouseFullyRequest(args));
                }
            }
        };

        const onInventoryDragStart = ({isSource}) => {
            if (!isSource) {
                return;
            }

            dispatch(setWarehouseFrom(warehouseData));
        };

        return (
            <>
                <WarehouseHeaderContainer
                    data={warehouseData}
                    warehouseIndex={warehouseIndex}
                    onEditWarehouseButtonClick={onEditWarehouseButtonClick}
                    isRenameWarehouseMode={isRenameWarehouseMode}
                    onWarehouseRename={onWarehouseRename}
                    isEditWarehouseMode={isEditWarehouseMode}
                />
                <div className={s.warehouseWrapper}>
                    <Container
                        onDrop={onInventoryDrop}
                        onDragStart={onInventoryDragStart}
                        getChildPayload={(index) => data[index]}
                        dropPlaceholder={{ className: s.dropPlaceholder }}
                        behaviour="move"
                        groupName="inventories"
                    >
                        {data.map((item) => (
                            <Draggable data-id="inventory" draggable={draggable} key={item.guid}>
                                <Inventory
                                    inventory={item}
                                    warehouseId={warehouseData.id}
                                    warehouseLength={warehouseData.items.length}
                                    isEditWarehouseMode={isEditWarehouseMode}
                                />
                            </Draggable>
                        ))}
                    </Container>
                </div>
                {!isEditWarehouseMode && !filterValue && (
                    <WarehouseFooter warehouseData={warehouseData}/>
                )}
            </>
        );
    }
);

export class Warehouse extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isEditWarehouseMode: false,
            showOverlay: false,
            isItemListEmpty: true,
            isRenameWarehouseMode: false,
            isAddNewInventoryMode: false,
        };
        this.ref = React.createRef();
        this.onEditWarehouseButtonClick = this.onEditWarehouseButtonClick.bind(this);
        this.hideOverlay = this.hideOverlay.bind(this);
        this.onWarehouseRename = this.onWarehouseRename.bind(this);
        this.setIsEditWarehouseMode = this.setIsEditWarehouseMode.bind(this);
    }

    onWarehouseRename() {
        const {isRenameWarehouseMode} = this.state;
        this.setState(
            {
                isRenameWarehouseMode: !isRenameWarehouseMode,
            },
            () => {
                this.props.disableDrag(this.state.isRenameWarehouseMode);
            }
        );
        this.setIsEditWarehouseMode(false);
        this.hideOverlay();
    }

    onEditWarehouseButtonClick() {
        const rect = this.ref.current.getBoundingClientRect();
        this.setState({
            showOverlay: true,
            top: rect.top,
            left: rect.left,
            height: rect.height,
            width: rect.width,
        });

        this.setIsEditWarehouseMode(true);

        if (this.props.warehouseData.items.length === 0) {
            this.setState({
                height: '37px',
                isItemListEmpty: true,
            });
        } else {
            this.setState({
                height: 'auto',
            });
        }
        const isLeftModeModal = document.documentElement.clientWidth - rect.left - rect.width < 100;
        this.setState({
            isLeftModeModal,
        });
    }

    setIsEditWarehouseMode(value) {
        this.setState({
            isEditWarehouseMode: value,
        });
    }

    hideOverlay() {
        this.setState({
            showOverlay: false,
        });
    }

    renderWarehouse(draggable) {
        const {warehouseData, warehouseIndex, reOrderInventory} = this.props;

        return (
            <Card
                warehouseData={warehouseData}
                warehouseIndex={warehouseIndex}
                isEditWarehouseMode={this.state.isEditWarehouseMode}
                isRenameWarehouseMode={this.state.isRenameWarehouseMode}
                reOrderInventory={reOrderInventory}
                onEditWarehouseButtonClick={this.onEditWarehouseButtonClick}
                onWarehouseRename={this.onWarehouseRename}
                draggable={draggable}
                isAddNewInventoryMode={this.state.isAddNewInventoryMode}
            />
        );
    }

    render() {
        const isWarehouseEmpty = this.props.warehouseData.items.length === 0;

        return (
            <>
                <div
                    className={cx(s.warehouse, 'empty', {
                        [s.notEmpty]: !isWarehouseEmpty,
                        [s.empty]: isWarehouseEmpty,
                        [s.emptyWarehouseInEditMode]:
                        this.state.isItemListEmpty && this.state.isEditWarehouseMode,
                        nonDrag: this.props.isDragDisabled,
                    })}
                    ref={this.ref}
                >
                    {this.renderWarehouse(true)}
                </div>

                {this.state.showOverlay && !this.state.isDeleteMode && (
                    <Overlay
                        onClick={() => {
                            this.setState({showOverlay: false});
                            this.setIsEditWarehouseMode(false);
                        }}
                    >
                        <div
                            style={{
                                position: 'fixed',
                                left: this.state.left,
                                top: this.state.top,
                                width: this.state.width,
                                height: this.state.height,
                                padding: '4px 0',
                            }}
                            className={s.warehouse}
                        >
                            {this.renderWarehouse(false)}
                            {this.state.isEditWarehouseMode && (
                                <EditWarehouseModal
                                    deleteWarehouse={this.props.deleteWarehouse}
                                    hideOverlay={this.hideOverlay}
                                    warehouse={this.props.warehouseData}
                                    onWarehouseRename={this.onWarehouseRename}
                                    isLeftModeModal={this.state.isLeftModeModal}
                                    setIsEditWarehouseMode={this.setIsEditWarehouseMode}
                                />
                            )}
                        </div>
                    </Overlay>
                )}
            </>
        );
    }
}

export function WarehouseContainer(props) {
    const dispatch = useDispatch();

    const isDragAvailable = useSelector((state) => state.warehouse.isDragAvailable);

    return (
        <Warehouse
            {...props}
            isDragDisabled={isDragAvailable}
            reOrderInventory={useCallback(
                (payload) => {
                    dispatch(reOrderInventoryPosition(payload));
                },
                [dispatch]
            )}
            deleteWarehouse={useCallback(
                (payload) => {
                    dispatch(sendDeleteWarehouseRequest(payload));
                },
                [dispatch]
            )}
            disableDrag={useCallback(
                (payload) => {
                    dispatch(changeIsDragAvailable(payload));
                },
                [dispatch]
            )}
        />
    );
}
